import useReveal from "@/hooks/useReveal";
import { useLang } from "@/i18n/LangContext";

/* Exploded-view diagram of the self-adhesive PDLC smart film.
   Layer order matches the physical film (top → bottom):
   PE protective film / ITO / PDLC / ITO / OCA glue / PET / silica gel / PET release film */

const VIEW_W = 860;
const VIEW_H = 580;
const SHEET = { cx: 190, rx: 180, ry: 55, thick: 6 };
const TOP = 8;
const STEP = 62;
const LABEL_X = 470;

const LAYERS = [
  { key: "pe", fill: "url(#pgGradBlue)", faceOpacity: 0.62, stroke: "#7FB9DA", edge: "#9CCBE4" },
  { key: "ito-top", fill: "url(#pgGradGrey)", faceOpacity: 0.85, stroke: "#C2CBD3", edge: "#C8D1D9" },
  { key: "pdlc", fill: "url(#pgGradWhite)", faceOpacity: 0.96, stroke: "#C6D0D8", edge: "#CBD5DC" },
  { key: "ito-bottom", fill: "url(#pgGradGrey)", faceOpacity: 0.85, stroke: "#C2CBD3", edge: "#C8D1D9" },
  { key: "oca", fill: "url(#pgGradGrey)", faceOpacity: 0.45, stroke: "#CDD6DD", edge: "#D5DEE4" },
  { key: "pet", fill: "url(#pgGradGrey)", faceOpacity: 0.8, stroke: "#C2CBD3", edge: "#C8D1D9" },
  { key: "silica", fill: "url(#pgGradBlue)", faceOpacity: 0.38, stroke: "#A9CFE5", edge: "#BBDAEC" },
  { key: "release", fill: "url(#pgGradBlue)", faceOpacity: 0.62, stroke: "#7FB9DA", edge: "#9CCBE4" },
];

function sheetPaths(y0) {
  const { cx, rx, ry, thick } = SHEET;
  const face = `M ${cx},${y0} L ${cx + rx},${y0 + ry} L ${cx},${y0 + 2 * ry} L ${cx - rx},${y0 + ry} Z`;
  const edge = `M ${cx - rx},${y0 + ry} L ${cx},${y0 + 2 * ry} L ${cx + rx},${y0 + ry} L ${cx + rx},${y0 + ry + thick} L ${cx},${y0 + 2 * ry + thick} L ${cx - rx},${y0 + ry + thick} Z`;
  return { face, edge };
}

export default function PdlcTechnology() {
  const { t } = useLang();
  const title = useReveal();
  const diagram = useReveal({ threshold: 0.25 });
  const revealed = diagram.className.includes("revealed");
  const labels = t("pdlc.layers");

  const layerStyle = (i, isLabel = false) => ({
    opacity: revealed ? 1 : 0,
    transform: revealed
      ? isLabel
        ? "translateY(-50%)"
        : "translateY(0px)"
      : isLabel
        ? "translateY(calc(-50% - 28px))"
        : "translateY(-28px)",
    transition: "transform 0.65s cubic-bezier(0.22,1,0.36,1), opacity 0.55s ease",
    transitionDelay: `${i * 110}ms`,
  });

  return (
    <section
      id="pdlc"
      data-testid="pdlc-technology-section"
      className="pt-10 md:pt-12 lg:pt-14 pb-24 md:pb-32 lg:pb-40 bg-[#FAFAFA]"
    >
      <div className="max-w-7xl mx-auto px-6 md:px-10">
        <div
          data-testid="pdlc-divider"
          className="h-px bg-[#1D1D1F]/10 mb-14 md:mb-20"
        />
      </div>
      <div className="max-w-7xl mx-auto px-6 md:px-10">
        <div ref={title.ref} className={`${title.className} max-w-3xl mb-12 md:mb-16`}>
          <h2
            data-testid="pdlc-title"
            className="font-display font-light tracking-tight text-4xl md:text-5xl lg:text-6xl text-[#1D1D1F] leading-[1.05]"
          >
            {t("pdlc.title")}
          </h2>
        </div>

        <div
          ref={diagram.ref}
          data-testid="pdlc-exploded-diagram"
          className="relative w-full max-w-[820px] mx-auto"
          style={{ aspectRatio: `${VIEW_W} / ${VIEW_H}` }}
        >
          <svg
            viewBox={`0 0 ${VIEW_W} ${VIEW_H}`}
            preserveAspectRatio="xMidYMid meet"
            className="absolute inset-0 w-full h-full"
            aria-hidden="true"
          >
            <defs>
              <linearGradient id="pgGradBlue" x1="0" y1="0" x2="1" y2="1">
                <stop offset="0%" stopColor="#D4EDFA" />
                <stop offset="55%" stopColor="#AFDCF2" />
                <stop offset="100%" stopColor="#9BD2EE" />
              </linearGradient>
              <linearGradient id="pgGradGrey" x1="0" y1="0" x2="1" y2="1">
                <stop offset="0%" stopColor="#F2F5F7" />
                <stop offset="100%" stopColor="#DBE2E8" />
              </linearGradient>
              <linearGradient id="pgGradWhite" x1="0" y1="0" x2="1" y2="1">
                <stop offset="0%" stopColor="#FFFFFF" />
                <stop offset="100%" stopColor="#ECF1F4" />
              </linearGradient>
            </defs>
            {LAYERS.slice()
              .reverse()
              .map((layer, ri) => {
                const i = LAYERS.length - 1 - ri;
                const y0 = TOP + i * STEP;
                const { face, edge } = sheetPaths(y0);
                const vy = y0 + SHEET.ry;
                return (
                  <g key={layer.key} style={layerStyle(i)}>
                    <path d={edge} fill={layer.edge} fillOpacity={Math.min(1, layer.faceOpacity + 0.1)} />
                    <path
                      d={face}
                      fill={layer.fill}
                      fillOpacity={layer.faceOpacity}
                      stroke={layer.stroke}
                      strokeOpacity="0.8"
                      strokeWidth="1"
                    />
                    <line
                      x1={SHEET.cx + SHEET.rx + 10}
                      y1={vy}
                      x2={LABEL_X - 12}
                      y2={vy}
                      stroke="#1D1D1F"
                      strokeOpacity="0.3"
                      strokeWidth="1"
                    />
                    <circle cx={SHEET.cx + SHEET.rx + 4} cy={vy} r="3.5" fill="#1D1D1F" fillOpacity="0.75" />
                  </g>
                );
              })}
          </svg>

          {Array.isArray(labels) &&
            labels.map((label, i) => {
              const vy = TOP + i * STEP + SHEET.ry;
              return (
                <div
                  key={i}
                  data-testid={`pdlc-layer-label-${i}`}
                  className="absolute font-body text-[#1D1D1F]/80 text-[10px] sm:text-xs md:text-sm lg:text-[15px] leading-none whitespace-nowrap"
                  style={{
                    left: `${(LABEL_X / VIEW_W) * 100}%`,
                    top: `${(vy / VIEW_H) * 100}%`,
                    ...layerStyle(i, true),
                  }}
                >
                  {label}
                </div>
              );
            })}
        </div>
      </div>
    </section>
  );
}
